import React, { useState } from 'react';

import { createStyles, Divider, Group, Skeleton, Stack, Text, Title } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconLock } from '@tabler/icons-react';
import HelpButton from 'components/HelpButton';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useGym from 'hooks/gym/useGym';
import useArm from 'hooks/reports/tbb-metrics/useArm';
import useAverageChurn from 'hooks/reports/tbb-metrics/useAverageChurn';
import useChurn from 'hooks/reports/tbb-metrics/useChurn';
import { useNavigate } from 'react-router-dom';

import { useDashboardStyles } from '../styles';

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `'armTotal armPaying churn avgChurn leg ltv'`,
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateAreas: `
        'armTotal churn leg'
        'armPaying avgChurn ltv'
        `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gridTemplateAreas: `
        'armTotal armPaying'
        'churn avgChurn'
        'leg ltv'
        `,
    },
  },
  armTotal: {
    gridArea: 'armTotal',
  },
  armPaying: {
    gridArea: 'armPaying',
  },
  churn: {
    gridArea: 'churn',
  },
  avgChurn: {
    gridArea: 'avgChurn',
  },
  leg: {
    gridArea: 'leg',
  },
  ltv: {
    gridArea: 'ltv',
  },
}));

const BusinessMetricsCard = (): JSX.Element => {
  const { classes: dashboard } = useDashboardStyles();
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const navigate = useNavigate();

  const { session, hasPerm } = useAuth();
  const { formatCurrency } = useLocalization();
  const lastMonth = new Date();
  lastMonth.setDate(0);

  const [month, setMonth] = useState<Date | null>(lastMonth);

  const hasPermission = hasPerm('BusinessMetricsReportsView');

  const { data: gymData } = useGym(session?.gym?.gymId);

  const {
    data: armTotal,
    isLoading: armTotalIsLoading,
    isError: armTotalIsError,
    isSuccess: armTotalIsSuccess,
  } = useArm({
    month: dayjs(month).format('YYYY-MM'),
    type: 'total',
    enabled: hasPermission,
  });

  const {
    data: armPaying,
    isLoading: armPayingIsLoading,
    isError: armPayingIsError,
    isSuccess: armPayingIsSuccess,
  } = useArm({
    month: dayjs(month).format('YYYY-MM'),
    type: 'paying',
    enabled: hasPermission,
  });

  const {
    data: churn,
    isLoading: churnIsLoading,
    isError: churnIsError,
    isSuccess: churnIsSuccess,
  } = useChurn({
    month: dayjs(month).format('YYYY-MM'),
    enabled: hasPermission,
  });

  const {
    data: averageChurn,
    isLoading: averageChurnIsLoading,
    isError: averageChurnIsError,
    isSuccess: averageChurnIsSuccess,
  } = useAverageChurn({
    month: dayjs(month).format('YYYY-MM'),
    enabled: hasPermission,
  });

  const handleReportClick = (report: string): void => {
    if (hasPermission) {
      navigate(`/reports/${report}`);
    }
  };

  const getArmTotal = (): JSX.Element => {
    if (!hasPermission) {
      return <IconLock size='2rem' />;
    }
    if (armTotalIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (armTotalIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (armTotalIsSuccess && armTotal) {
      return <div className={dashboard.value}>{formatCurrency(armTotal.data.arm / 100)}</div>;
    }
    return <></>;
  };

  const getArmPaying = (): JSX.Element => {
    if (!hasPermission) {
      return <IconLock size='2rem' />;
    }
    if (armPayingIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (armPayingIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (armPayingIsSuccess && armPaying) {
      return <div className={dashboard.value}>{formatCurrency(armPaying.data.arm / 100)}</div>;
    }
    return <></>;
  };

  const getChurn = (): JSX.Element => {
    if (!hasPermission) {
      return <IconLock size='2rem' />;
    }
    if (churnIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (churnIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (churnIsSuccess && churn) {
      return <div className={dashboard.value}>{`${churn.data.churn.toFixed(2)}%`}</div>;
    }
    return <></>;
  };

  const getChurnSummary = (): JSX.Element => {
    const prevMonth = new Date(month || lastMonth);
    prevMonth.setDate(0);
    if (hasPermission && churnIsSuccess && churn) {
      return (
        <Text size='sm'>{`${churn.data.canceledMembers} Cancelled Members in ${dayjs(month).format(
          'MMMM',
        )} / ${churn.data.totalMembers} Paying Members in ${dayjs(prevMonth).format('MMMM')}`}</Text>
      );
    }
    return <></>;
  };

  const getAverageChurn = (): JSX.Element => {
    if (!hasPermission) {
      return <IconLock size='2rem' />;
    }
    if (averageChurnIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (averageChurnIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (averageChurnIsSuccess && averageChurn) {
      return <div className={dashboard.value}>{`${averageChurn.data.averageChurn.toFixed(2)}%`}</div>;
    }
    return <></>;
  };

  const getEstimatedLeg = (): JSX.Element => {
    if (!hasPermission) {
      return <IconLock size='2rem' />;
    }
    if (averageChurnIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (averageChurnIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (averageChurnIsSuccess && averageChurn && averageChurn.data.averageChurn > 0) {
      return (
        <div className={dashboard.value}>{`${(100 / averageChurn.data.averageChurn).toFixed(
          2,
        )} Months`}</div>
      );
    }
    if (averageChurnIsSuccess && averageChurn && averageChurn.data.averageChurn === 0) {
      return <div className={dashboard.value}>N/A</div>;
    }
    return <></>;
  };

  const getAverageLTV = (): JSX.Element => {
    if (!hasPermission) {
      return <IconLock size='2rem' />;
    }
    if (armPayingIsLoading || averageChurnIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (armPayingIsError || averageChurnIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (averageChurnIsSuccess && averageChurn && averageChurn.data.averageChurn === 0) {
      return <div className={dashboard.value}>N/A</div>;
    }
    if (
      armPayingIsSuccess &&
      averageChurnIsSuccess &&
      armPaying &&
      averageChurn &&
      averageChurn.data.averageChurn > 0
    ) {
      return (
        <div className={dashboard.value}>
          {formatCurrency(armPaying.data.arm * (1 / averageChurn.data.averageChurn))}
        </div>
      );
    }
    return <></>;
  };

  return (
    <div className={`${dashboard.metrics} ${dashboard.card}`}>
      <div className={dashboard.cardHeader}>
        <Group>
          <Title order={4}>Business Metrics</Title>
          <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'} size='sm'>
            <Text size='sm' sx={{ whiteSpace: 'normal' }}>
              The Business Metrics collect data from completed months.
            </Text>
          </HelpButton>
        </Group>
        {hasPermission ? (
          <MonthPickerInput
            value={month}
            onChange={setMonth}
            valueFormat='MMMM YYYY'
            minDate={
              new Date(
                gymData?.data.additionalSettings.businessMetricsStartDate || gymData?.data.createdAt || '',
              )
            }
            maxDate={lastMonth}
            allowDeselect={false}
            required
          />
        ) : (
          <IconLock size='2rem' />
        )}
      </div>

      <Divider />

      <div className={classes.grid}>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.armTotal} ${hasPermission ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('arm/total')}
        >
          <div className={dashboard.label}>ARM Total</div>
          {getArmTotal()}
        </Stack>

        <Stack
          spacing={0}
          p='xs'
          className={`${classes.armPaying} ${hasPermission ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('arm/paying')}
        >
          <div className={dashboard.label}>ARM Paying</div>
          {getArmPaying()}
        </Stack>

        <Stack spacing={0} p='xs' className={classes.churn}>
          <Group noWrap spacing='xs'>
            <div className={dashboard.label}>Churn</div>
            <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'} size='sm' withinPortal>
              <Text size='sm' sx={{ whiteSpace: 'normal', paddingBlockEnd: theme.spacing.xs }}>
                Churn is calculated by the number of cancelled members in a month divided by the number of
                paying members at the end of the previous month.
              </Text>
              {getChurnSummary()}
            </HelpButton>
          </Group>
          {getChurn()}
        </Stack>

        <Stack spacing={0} p='xs' className={classes.avgChurn}>
          <Group noWrap spacing='xs'>
            <div className={dashboard.label}>Average Churn</div>
            <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'} size='sm'>
              <Text size='sm' sx={{ whiteSpace: 'normal' }}>
                Average Churn is calculated by taking the average of the past six months of churn.
              </Text>
            </HelpButton>
          </Group>
          {getAverageChurn()}
        </Stack>

        <Stack spacing={0} p='xs' className={classes.leg}>
          <Group noWrap spacing='xs'>
            <div className={dashboard.label}>Estimated LEG</div>
            <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'} size='sm'>
              <Text size='sm' sx={{ whiteSpace: 'normal' }}>
                Estimated LEG (length of engagement) is calculated by using the formula 1 / Average Churn
              </Text>
            </HelpButton>
          </Group>
          {getEstimatedLeg()}
        </Stack>

        <Stack spacing={0} p='xs' className={classes.ltv}>
          <Group noWrap spacing='xs'>
            <div className={dashboard.label}>Average LTV</div>
            <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'} size='sm'>
              <Text size='sm' sx={{ whiteSpace: 'normal' }}>
                Average LTV (lifetime value) is calculated by using the formula ARM Paying x Estimated LEG
              </Text>
            </HelpButton>
          </Group>
          {getAverageLTV()}
        </Stack>
      </div>
    </div>
  );
};

export default BusinessMetricsCard;
